import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const GlowButtonWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  cursor: 'pointer',
  animation: 'bounce-float 3s ease-in-out infinite',
  
  '@keyframes bounce-float': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
  },
}));

const PulseRing = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '50%',
  opacity: 0.6,
  backgroundColor: '#ff8c00',
  filter: 'blur(16px)',
  animation: 'pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  
  '@keyframes pulse-ring': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(255, 165, 0, 0.7)',
    },
    '70%': {
      transform: 'scale(1.05)',
      boxShadow: '0 0 0 20px rgba(255, 165, 0, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(255, 165, 0, 0)',
    },
  },
}));

const GlowButton = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: '12px 20px',
  background: 'linear-gradient(to bottom, #FFD700, #FF8C00, #FF4500)',
  borderRadius: '50px',
  border: '1px solid rgba(255, 215, 0, 0.5)',
  boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3), inset 0 -4px 6px rgba(0, 0, 0, 0.2)',
  overflow: 'hidden',
  transition: 'transform 0.1s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const GlossyHighlight = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '50%',
  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.6), transparent)',
  borderTopLeftRadius: '50%',
  borderTopRightRadius: '50%',
  margin: '4px',
  pointerEvents: 'none',
  zIndex: 20,
}));

const RippleSweep = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 10,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '50%',
    height: '100%',
    background: 'linear-gradient(to right, transparent, rgba(255, 255, 255, 0.4), transparent)',
    filter: 'blur(2px)',
    animation: 'ripple-sweep 2s infinite linear',
  },
  
  '@keyframes ripple-sweep': {
    '0%': {
      transform: 'translateX(-100%) skewX(-12deg)',
    },
    '100%': {
      transform: 'translateX(200%) skewX(-12deg)',
    },
  },
}));

const FloatingParticle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  backgroundColor: '#ffffff',
  borderRadius: '50%',
  pointerEvents: 'none',
  zIndex: 30,
  animation: 'float-particle 2s infinite ease-out',
  
  '@keyframes float-particle': {
    '0%': {
      transform: 'translateY(0) scale(1)',
      opacity: 0,
    },
    '20%': {
      opacity: 1,
    },
    '80%': {
      opacity: 1,
    },
    '100%': {
      transform: 'translateY(-40px) scale(0)',
      opacity: 0,
    },
  },
}));

const ButtonText = styled(Typography)(({ theme }) => ({
  position: 'relative',
  zIndex: 40,
  fontSize: '16px',
  fontWeight: 900,
  color: '#ffffff',
  textShadow: '0 2px 2px rgba(0, 0, 0, 0.3)',
  letterSpacing: '0.05em',
}));

const GlowButtonComponent: React.FC = () => {
  return (
    <Box 
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        fontFamily: 'var(--font-noto)',
      }}
    >
      <style>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 165, 0, 0.7);
          }
          70% {
            transform: scale(1.05);
            box-shadow: 0 0 0 20px rgba(255, 165, 0, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 165, 0, 0);
          }
        }
        
        @keyframes float-particle {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          20% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translateY(-40px) scale(0);
            opacity: 0;
          }
        }

        @keyframes ripple-sweep {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(200%) skewX(-12deg);
          }
        }

        @keyframes bounce-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>

      <GlowButtonWrapper>
        <PulseRing />
        
        <GlowButton>
          <GlossyHighlight />
          <RippleSweep />
          
          <FloatingParticle 
            sx={{
              top: '50%',
              left: '25%',
              width: '8px',
              height: '8px',
              animationDelay: '0s',
            }} 
          />
          <FloatingParticle 
            sx={{
              top: '75%',
              right: '25%',
              width: '6px',
              height: '6px',
              animationDelay: '1s',
              animationDuration: '2.5s',
            }} 
          />
          <FloatingParticle 
            sx={{
              top: '66%',
              right: '33%',
              width: '4px',
              height: '4px',
              animationDelay: '0.5s',
              animationDuration: '3s',
            }} 
          />

          <ButtonText variant="h5">
            คลิกเลย
          </ButtonText>
        </GlowButton>
      </GlowButtonWrapper>
    </Box>
  );
};

export default GlowButtonComponent;